import { useState } from 'react';
import { X, Mail, AlertTriangle } from 'lucide-react';
import { MaintenanceRequest } from '../../types/maintenance';
import { Partner } from '../../types/maintenance';

interface SendQuotationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  request: MaintenanceRequest | null;
  partners: Partner[];
  loading?: boolean;
}

export default function SendQuotationsModal({
  isOpen,
  onClose,
  onConfirm,
  request,
  partners,
  loading = false
}: SendQuotationsModalProps) {
  const [confirmed, setConfirmed] = useState(false);

  const handleClose = () => {
    setConfirmed(false);
    onClose();
  };

  if (!isOpen || !request) return null;

  const handleSubmit = () => {
    if (!confirmed) return;
    onConfirm();
  };

  // Filter active partners
  const activePartners = partners.filter(p => p.status === 'ativo');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity" onClick={handleClose} />

        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Mail className="h-5 w-5 mr-2 text-blue-600" />
              Enviar Pedidos de Cotação
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
              disabled={loading}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Request Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-md">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Ativo:</span>{' '}
                <span className="text-gray-900">{request.equipmentId}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Descrição:</span>{' '}
                <span className="text-gray-900">{request.equipmentDescription || 'N/A'}</span>
              </div>
              <div className="col-span-2">
                <span className="font-medium text-gray-700">Problema:</span>{' '}
                <span className="text-gray-900">{request.problemDescription}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Custo Estimado:</span>{' '}
                <span className="text-gray-900">
                  {request.estimatedCost ? `€${Number(request.estimatedCost).toFixed(2)}` : 'N/A'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Urgência:</span>{' '}
                <span className="text-gray-900 capitalize">{request.urgency}</span>
              </div>
            </div>
          </div>

          {/* Confirmation Message */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">Confirme a necessidade desta compra</p>
                <p className="text-blue-800">
                  Ao clicar em &quot;Enviar Cotações&quot;, os parceiros abaixo receberão um email solicitando orçamento para este serviço.
                  As respostas deverão ser enviadas diretamente para o seu email.
                </p>
              </div>
            </div>
          </div>

          {/* Partners List */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">
              Parceiros que receberão o pedido ({activePartners.length})
            </h4>
            {activePartners.length > 0 ? (
              <div className="border border-gray-200 rounded-md overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-700 uppercase">
                    <div className="col-span-4">Nome</div>
                    <div className="col-span-4">Email</div>
                    <div className="col-span-2">Telefone</div>
                    <div className="col-span-2">Especialização</div>
                  </div>
                </div>
                <div className="divide-y divide-gray-200 max-h-60 overflow-y-auto">
                  {activePartners.map((partner, index) => (
                    <div key={partner.partnerId || index} className="px-4 py-3">
                      <div className="grid grid-cols-12 gap-4 text-sm">
                        <div className="col-span-4 font-medium text-gray-900">{partner.name}</div>
                        <div className="col-span-4 text-gray-600">{partner.primaryEmail}</div>
                        <div className="col-span-2 text-gray-600">{partner.primaryPhone}</div>
                        <div className="col-span-2 text-gray-600">{partner.specialization}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-md border border-dashed border-gray-300">
                <AlertTriangle className="h-10 w-10 text-yellow-500 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Nenhum parceiro ativo encontrado para esta categoria de equipamento.</p>
              </div>
            )}
          </div>

          {/* Confirmation Checkbox */}
          <div className="mb-6">
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                disabled={loading}
              />
              <span className="ml-3 text-sm text-gray-700">
                Confirmo que esta compra é necessária e desejo enviar pedidos de cotação aos parceiros listados acima.
              </span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!confirmed || loading || activePartners.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <span className="mr-2">Enviando...</span>
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Enviar Cotações
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
